import RecordedChampion from '../../../components/RecordedChampion.jsx'
import { useMemo, useState } from 'react'
import {
  needsAssignment, seededWorklist, SEEDED_COUNT, SINGLE_POINTS, REP_COUNT, TEAM_COUNT,
} from '../assignments.js'
import { formatDate, sportMeta } from '../../../lib/championships.js'
import { SelectAllTh, SelectCell } from './RowSelect.jsx'
import { SuggestedRep, RepPicker, MapProvenance } from './AssignRep.jsx'
import styles from './assignments.module.css'
import RuleAssignments from './RuleAssignments.jsx'

/**
 * THE ASSIGNMENTS WORKLIST — who has nobody on them, and who should.
 *
 * Two lists, because they are two different things and conflating them would
 * be dishonest:
 *
 *  1. NEEDS A REP — live records in this space with no `sr_assigned_rep`. These
 *     have a HubSpot id, so they are the ones a reassignment can actually
 *     write. This is the working list.
 *  2. SEEDED — the 59 no-rep WON accounts the routing map ships with. They are
 *     ClickUp account records, not HubSpot 2-64425303 rows: there is no id to
 *     write against, so each one links out to ClickUp and offers no button.
 *     Stating that beats rendering an Assign control that would 400.
 *
 * Every row carries its proposal and the reason for it. Nothing on this page
 * writes without a human click, and only a human holding "Manage Assignments"
 * sees a control at all.
 */
export default function Assignments({
  items, onOpen, selection = null, gate, onAssign, busyId = null, noun = 'Championship', ruleRun = null,
}) {
  const [tab, setTab] = useState('live')
  const [limit, setLimit] = useState(100)

  const unassigned = useMemo(() => items.filter(needsAssignment), [items])
  const seeded = useMemo(() => seededWorklist(), [])
  const list = tab === 'live' ? unassigned : seeded
  const shown = limit >= list.length ? list : list.slice(0, limit)

  const covered = items.length - unassigned.length

  return (
    <div className={styles.wrap}>
      {ruleRun && <RuleAssignments items={items} {...ruleRun} />}
      <div className={styles.head}>
        <div className={styles.headText}>
          <div className={styles.headTitle}>Assignments</div>
          <p className={styles.headSub}>
            Every record with nobody on it, and the rep the win history says has actually PROVEN that
            sport and region. Suggestions are display-only — a proposal never writes, and a record
            that already has a rep assigned is never re-proposed. The picker itself offers the{' '}
            <strong>whole SR team</strong>, grouped by what they do, so a record can go to a
            designer or the cheer team and not only to a rep. Assigning writes{' '}
            <code>sr_assigned_rep</code> and is recorded in the audit trail.
          </p>
          <MapProvenance />
        </div>
        <div className={styles.kpis}>
          <div className={styles.kpi}>
            <div className={styles.kpiNum}>{unassigned.length.toLocaleString()}</div>
            <div className={styles.kpiLabel}>need a rep</div>
          </div>
          <div className={styles.kpi}>
            <div className={styles.kpiNum}>{covered.toLocaleString()}</div>
            <div className={styles.kpiLabel}>already owned</div>
          </div>
          <div className={styles.kpi}>
            <div className={styles.kpiNum}>{SEEDED_COUNT.toLocaleString()}</div>
            <div className={styles.kpiLabel}>seeded no-rep wins</div>
          </div>
          <div className={styles.kpi}>
            <div className={styles.kpiNum}>{TEAM_COUNT}</div>
            <div className={styles.kpiLabel}>assignable team</div>
          </div>
          <div className={styles.kpi}>
            <div className={styles.kpiNum}>{REP_COUNT}</div>
            <div className={styles.kpiLabel}>reps in the map</div>
          </div>
        </div>
      </div>

      {/* SINGLE-POINT DEPENDENCIES — flags, not blockers. The routing still
          sends cheer to Jennifer; this is the reason to cross-train a backup. */}
      {SINGLE_POINTS.length > 0 && (
        <div className={styles.flags}>
          <div className={styles.flagsHead}>Single-point dependencies</div>
          {SINGLE_POINTS.map((d) => (
            <div key={`${d.area}-${d.owner}`} className={styles.flagRow}>
              <strong>{d.area}</strong> — {d.owner}
              {d.concentration_pct != null && ` (${d.concentration_pct}%)`} · {d.detail}
            </div>
          ))}
        </div>
      )}

      <div className={styles.tabs}>
        <button
          type="button"
          className={`${styles.tab} ${tab === 'live' ? styles.tabOn : ''}`}
          onClick={() => { setTab('live'); setLimit(100) }}
        >
          Needs a rep · {unassigned.length.toLocaleString()}
        </button>
        <button
          type="button"
          className={`${styles.tab} ${tab === 'seeded' ? styles.tabOn : ''}`}
          onClick={() => { setTab('seeded'); setLimit(100) }}
        >
          Seeded no-rep wins · {seeded.length.toLocaleString()}
        </button>
      </div>

      {tab === 'seeded' && (
        <p className={styles.note}>
          These {seeded.length} accounts come from the routing map itself — won ClickUp records with no
          rep on them. They are ClickUp tasks, not HubSpot championship rows, so there is no record here
          to write to: each links out to ClickUp, where the assignment is made. The proposal is the same
          engine the live list uses.
        </p>
      )}

      {/* Cards below 640px. This was the widest table in the Command Center
          (723px of content in a 350px viewport), and the two columns that
          matter — the engine's suggestion and the picker that acts on it —
          were the two furthest off-screen. See src/styles/cc-mobile.css. */}
      <div className={`${styles.tableWrap} cc-scrollcue cc-cards-wrap`}>
        {tab === 'live' ? (
          <table className={`${styles.table} cc-cards`}>
            <thead>
              <tr>
                <SelectAllTh rows={unassigned} selection={selection} label="records needing a rep" />
                <th>{noun}</th>
                {shown.some(c=>!c.sourceType) && <th>Champion / recorded claim</th>}
                <th>Sport / level</th>
                <th>State</th>
                <th>Event date</th>
                <th>Suggested rep</th>
                <th>Assign</th>
              </tr>
            </thead>
            <tbody>
              {shown.map((c) => {
                const sm = sportMeta(c.sport)
                return (
                  <tr
                    key={c.id}
                    className={onOpen ? styles.rowClickable : ''}
                    onClick={onOpen ? () => onOpen(c) : undefined}
                  >
                    <SelectCell item={c} selection={selection} />
                    <td className={styles.name} data-col="name">
                      {c.name}{onOpen && <span className={styles.openHint}> ›</span>}
                    </td>
                    {shown.some(row=>!row.sourceType) && <td data-col="champion" data-label="Champion / recorded claim"><RecordedChampion record={c} /></td>}
                    <td className={styles.dim} data-col="sport" data-label="Sport / level">
                      {c.sport && <span style={{ color: sm.color }}>● </span>}
                      {[c.sport, c.level].filter(Boolean).join(' · ') || '—'}
                    </td>
                    <td className={styles.dim} data-col="state" data-label="State">{c.state && c.state !== 'NATIONAL' ? c.state : '—'}</td>
                    <td className={styles.dim} data-col="date" data-label="Event date">{c.effDate ? formatDate(c.effDate) : 'NO DATE'}</td>
                    <td data-col="suggest" data-label="Suggested rep"><SuggestedRep record={c} /></td>
                    <td data-col="cta" data-label="Assign" onClick={(e) => e.stopPropagation()}>
                      <RepPicker record={c} gate={gate} onAssign={onAssign} busy={busyId === c.id} />
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        ) : (
          <table className={`${styles.table} cc-cards`}>
            <thead>
              <tr>
                <th>Account</th>
                <th>Sport</th>
                <th>State</th>
                <th>Status</th>
                <th>Suggested rep</th>
                <th>Confidence</th>
                <th>ClickUp</th>
              </tr>
            </thead>
            <tbody>
              {shown.map((row) => (
                <tr key={row.url || row.account}>
                  <td className={styles.name} data-col="name">{row.account}</td>
                  <td className={styles.dim} data-col="sport" data-label="Sport">{row.sport || '—'}</td>
                  <td className={styles.dim} data-col="state" data-label="State">{row.state || '—'}</td>
                  <td className={styles.dim} data-col="status" data-label="Status">{row.status || '—'}</td>
                  <td data-col="suggest" data-label="Suggested rep">
                    {row.proposal?.noMatch
                      ? <span className={styles.err}>No proven match — needs manual assignment.</span>
                      : <span><strong>{row.proposal?.suggestedDisplayName}</strong> — <span className={styles.dim}>{row.proposal?.why}</span></span>}
                  </td>
                  <td className={styles.dim} data-col="confidence" data-label="Confidence">{row.proposal?.confidence || 'none'}</td>
                  {/* A seeded win is a ClickUp task with no HubSpot row, so
                      there is nothing to assign — the link out is the action. */}
                  <td data-col="cta" data-label="ClickUp">
                    {row.url
                      ? <a className={styles.link} href={row.url} target="_blank" rel="noopener noreferrer">Open ↗</a>
                      : <span className={styles.dim}>—</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {list.length > shown.length && (
          <div className={styles.more}>
            <button className={styles.moreBtn} onClick={() => setLimit(limit + 500)}>
              Show {Math.min(500, list.length - shown.length).toLocaleString()} more
            </button>
            <button className={styles.moreBtn} onClick={() => setLimit(list.length)}>
              Show all {list.length.toLocaleString()}
            </button>
          </div>
        )}
        {list.length === 0 && (
          <div className={styles.empty}>
            {tab === 'live'
              ? 'Every record in this scope has a rep on it.'
              : 'The routing map carries no seeded no-rep accounts.'}
          </div>
        )}
      </div>
    </div>
  )
}
